export type ForEachItem = {
  label?: string;
  input?: Record<string, unknown>;
};

type ForEachIncompleteMarker = {
  subAgentChatIds?: string[];
  '@@cotera-tool-state'?: string;
};

function parseSubAgentChatIdsFromIncompleteMarker(raw: unknown): string[] {
  if (raw === undefined || raw === null) {
    return [];
  }

  let incomplete: ForEachIncompleteMarker | undefined;
  if (typeof raw === 'string') {
    try {
      incomplete = JSON.parse(raw) as ForEachIncompleteMarker;
    } catch {
      return [];
    }
  } else if (typeof raw === 'object') {
    incomplete = raw as ForEachIncompleteMarker;
  }

  if (incomplete !== undefined && Array.isArray(incomplete.subAgentChatIds)) {
    return incomplete.subAgentChatIds.filter(
      (x): x is string => typeof x === 'string'
    );
  }
  return [];
}

function parseSubAgentChatIdsFromToolOutput(output: unknown): string[] {
  if (
    output === null ||
    output === undefined ||
    typeof output !== 'object' ||
    Array.isArray(output)
  ) {
    return [];
  }
  const o = output as Record<string, unknown>;
  const direct = o['subAgentChatIds'];
  if (
    Array.isArray(direct) &&
    direct.every((x): x is string => typeof x === 'string')
  ) {
    return direct;
  }
  return parseSubAgentChatIdsFromIncompleteMarker(
    o['@@cotera-tool-incomplete']
  );
}

/** Minimal surface used by {@link extractForEachData} (implemented by {@link ToolPart}). */
export type ForEachToolPartLike = {
  readonly input: Record<string, unknown> | undefined;
  getOutput: () => unknown;
  raw: () => unknown;
};

/**
 * Reads for-each `items` and child sub-agent conversation ids from a tool part.
 * Prefer `@@cotera-tool-incomplete` on input (live conversation updates); fall back to
 * `output.subAgentChatIds` so nested / snapshot-loaded messages still resolve ids when the
 * streamed tool **result** row carries them without merged input (see coco/for-each tool).
 */
export function extractForEachData(part: ForEachToolPartLike): {
  chatIds: string[];
  items: ForEachItem[];
} {
  const input = part.input as Record<string, unknown> | undefined;
  const items =
    input !== undefined && Array.isArray(input['items'])
      ? (input['items'] as ForEachItem[])
      : [];

  const fromInput =
    input !== undefined
      ? parseSubAgentChatIdsFromIncompleteMarker(
          input['@@cotera-tool-incomplete']
        )
      : [];

  if (fromInput.length > 0) {
    return { chatIds: fromInput, items };
  }

  const fromOutput = parseSubAgentChatIdsFromToolOutput(part.getOutput());
  return { chatIds: fromOutput, items };
}

/** Stable props fingerprint for memoizing for-each tool UI. */
export function forEachToolPartRevisionKey(part: ForEachToolPartLike): string {
  const raw = part.raw() as Record<string, unknown>;
  return JSON.stringify({
    input: raw['input'],
    output: raw['output'],
    state: raw['state'],
  });
}
